import { cn, initials } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

export const Spinner = ({ className }: { className?: string }) => (
  <Loader2 className={cn("h-5 w-5 animate-spin text-muted-foreground", className)} />
);

export const Loading = ({ label = "Loading" }: { label?: string }) => (
  <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
    <Spinner /> {label}…
  </div>
);

export const EmptyState = ({
  icon,
  title,
  body,
  action,
}: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-10 text-center">
    {icon ? <div className="mb-3 text-muted-foreground">{icon}</div> : null}
    <h3 className="font-semibold">{title}</h3>
    {body ? <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p> : null}
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

export const ErrorBox = ({ message }: { message: string }) => (
  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
    {message}
  </div>
);

export const Avatar = ({
  name,
  src,
  className,
}: { name: string; src?: string; className?: string }) =>
  src ? (
    <img src={src} alt={name} className={cn("h-8 w-8 rounded-full object-cover", className)} />
  ) : (
    <div
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary",
        className,
      )}
    >
      {initials(name)}
    </div>
  );

export const Stat = ({
  label,
  value,
  hint,
  tone,
  onClick,
  active,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "success" | "warning" | "destructive";
  /** Clickable card: filters the list below to this figure. */
  onClick?: () => void;
  active?: boolean;
}) => (
  <div
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    title={onClick ? "Show these in the list" : undefined}
    className={cn(
      "rounded-lg border bg-card p-4",
      onClick && "cursor-pointer transition-colors hover:border-primary/60 hover:bg-accent/40",
      active && "border-primary ring-2 ring-primary/20",
    )}
  >
    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    <div
      className={cn(
        "mt-1 text-2xl font-semibold tabular-nums",
        tone === "success" && "text-success",
        tone === "warning" && "text-warning",
        tone === "destructive" && "text-destructive",
      )}
    >
      {value}
    </div>
    {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
  </div>
);

export const Tabs = <T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { value: T; label: string; count?: number }[];
}) => (
  <div className="flex gap-1 rounded-lg bg-muted p-1">
    {items.map((t) => (
      <button
        key={t.value}
        type="button"
        onClick={() => onChange(t.value)}
        className={cn(
          "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          value === t.value ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground",
        )}
      >
        {t.label}
        {t.count !== undefined ? (
          <span className="ml-1.5 text-xs text-muted-foreground">{t.count}</span>
        ) : null}
      </button>
    ))}
  </div>
);
